import classNames from "classnames"
import React, { useRef, useMemo } from "react"
import SwiperCore, { Navigation, Pagination, Controller } from "swiper"
import { Swiper, SwiperSlide } from "swiper/react"

import { Color } from "../../../constants/V2/color"
import PillButton from "../../elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "../../elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "../../elements/V2/Icons/ArrowIcon"
import CircleCheckIcon from "../../elements/V2/Icons/CircleCheckIcon"
import CircleCrossIcon from "../../elements/V2/Icons/CircleCrossIcon"
import Logo from "../../elements/V2/Icons/YocoLogoIcon"
import Tooltip from "../../elements/V2/Tooltip"
import Typography from "../../elements/V2/Typography"

import CircleDashIcon from "@components/elements/V2/Icons/CircleDashIcon"
import { TableRegex } from "@constants/tables"
import { useGetProductBySlug } from "@hooks/V2/storyblok/useProduct"
import { checkPromotion } from "@utils/shop"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"
import { isTablet } from "@utils/V2/screen"

SwiperCore.use([Navigation, Pagination, Controller])

const CELL_CLASSES =
  "text-center flex items-center px-16 py-8 h-48 md:h-56 lg:h-[68px] rounded-8"
const CELL_TEXT_CLASSSES =
  "[display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] line-clamp-2 overflow-hidden text-ellipsis"
type Props = {
  sectionId: string
  numDesktopColumns?: 3 | 4 | 6
  columnImages?: string[]
  rowHeadings: string[]
  data: string[][]
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  outerContainer: "gridSection" | "tabSelector"
}

const SingleEntryComparisonTable = ({
  sectionId,
  numDesktopColumns = 4,
  columnImages,
  rowHeadings,
  data,
  backgroundColor,
  outerContainer,
}: Props) => {
  const tableTheme =
    backgroundColor === Color.Black || backgroundColor === Color.Charcoal
      ? "dark"
      : "light"
  const getProductBySlug = useGetProductBySlug()
  const headerSwiperRef = useRef<SwiperCore | null>(null)
  const titleRowIndices = useRef<number[]>([])
  const columnCount = useMemo(() => {
    let longestDataRow = 0
    data.forEach((dataRow) => {
      if (dataRow.length > longestDataRow) {
        longestDataRow = dataRow.length
      }
    })
    return longestDataRow
  }, [data])

  const RenderCellData = (rowIndex: number, columnIndex: number) => {
    const cellData = data[rowIndex][columnIndex]
    const isCheckIcon = cellData.match(TableRegex.CHECK_ICON)
    const isCheckIconBlackStroke = cellData.match(
      TableRegex.CHECK_ICON_BLACK_STROKE
    )
    const isCrossIcon = cellData.match(TableRegex.CROSS_ICON)
    const isDashIcon = cellData.match(TableRegex.DASH_ICON)
    const isList = cellData.match(TableRegex.LIST)
    const isPriceFromStoryblokSlug = cellData.match(
      TableRegex.PRICE_FROM_STORYBLOK_SLUG
    )
    const isDetailedPriceFromStoryblokSlug = cellData.match(
      TableRegex.DETAILED_PRICE_FROM_STORYBLOK_SLUG
    )
    const isPrice = cellData.match(TableRegex.PRICE)
    const isButton = cellData.match(TableRegex.BUTTON)
    const isHeader = cellData.match(TableRegex.HEADER)
    let productPrice = 0
    let list: string[] = []

    if (isList) {
      const fullMatch = isList[0]
      const listContent = fullMatch.substring(6, fullMatch.length - 2)
      list = listContent.split(",").map((item) => item.trim())
    }
    let priceSubScript
    let priceSideScript
    let promotionStartDate
    let promotionEndDate
    let promotionSavings = 0
    if (isPriceFromStoryblokSlug || isDetailedPriceFromStoryblokSlug) {
      const slug = cellData
        .replace(
          /:priceFromStoryblokSlug\(|:detailedPriceFromStoryblokSlug\(/,
          ""
        )
        .replace("):", "")

      const product = getProductBySlug(slug)

      if (product) {
        productPrice = Number(product.price)

        if (isDetailedPriceFromStoryblokSlug) {
          "priceSubScript" in product
            ? (priceSubScript = product.priceSubScript)
            : null
          "priceSideScript" in product
            ? (priceSideScript = product.priceSideScript)
            : null
          "savingsAmount" in product
            ? (promotionSavings = Number(product.savingsAmount))
            : null
          "promotionStartDate" in product
            ? (promotionStartDate = product.promotionStartDate)
            : null
          "promotionEndDate" in product
            ? (promotionEndDate = product.promotionEndDate)
            : null
        }
      }
    }
    const showPromotion = checkPromotion(promotionStartDate, promotionEndDate)
    let buttonLabel, buttonLink

    if (isButton) {
      const buttonData = cellData.replace(/:button\(/, "").replace("):", "")
      buttonLabel = buttonData.substring(0, buttonData.indexOf(","))
      buttonLink = buttonData.substring(
        buttonData.indexOf(",") + 1,
        buttonData.length
      )
    }
    return (
      <div
        key={`column-${columnIndex}-row-${rowIndex}`}
        className={classNames(
          CELL_CLASSES,
          "justify-center",
          { "sm:rounded-r-none": columnIndex === 0 },
          {
            "sm:rounded-l-none": columnIndex === columnCount - 1,
          },
          {
            "sm:rounded-none": columnIndex > 0 && columnIndex < columnCount - 1,
          },
          {
            "mt-16 md:mt-24": titleRowIndices.current.find(
              (titleRowIndex) => titleRowIndex === rowIndex
            ),
          },
          tableTheme === "light"
            ? "odd:bg-light-gray"
            : backgroundColor === Color.Charcoal
              ? "odd:bg-black"
              : "odd:bg-charcoal",
          { "mt-10 px-0": isButton }
        )}
      >
        {isCrossIcon ? (
          <div
            className={classNames(
              "flex items-center",
              isCrossIcon?.[1] && "w-full justify-self-start"
            )}
          >
            <CircleCrossIcon
              className={classNames(
                "shrink-0",
                tableTheme === "light" ? "stroke-charcoal" : "stroke-white"
              )}
            />
            {isCrossIcon?.[1] && (
              <Typography
                font="grotesk"
                weight="book"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size={isTablet() ? "subscript-sm" : "body-md"}
                text={isCrossIcon?.[1]}
                className="pl-16 text-start sm:pl-[2px] md:pl-10"
              />
            )}
          </div>
        ) : isCheckIcon ? (
          <div
            className={classNames(
              "flex items-center",
              isCheckIcon?.[2] && "w-full justify-start"
            )}
          >
            <CircleCheckIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={classNames(
                "shrink-0",
                isCheckIcon?.[1] === "yellow"
                  ? "fill-yellow"
                  : isCheckIcon?.[1] === "orange"
                    ? "fill-orange"
                    : isCheckIcon?.[1] === "blue"
                      ? "fill-blue"
                      : isCheckIcon?.[1] === "white"
                        ? "fill-white"
                        : undefined
              )}
              checkmarkClassName={
                isCheckIcon?.[1] === "white" ? "stroke-blue" : "stroke-white"
              }
            />
            {isCheckIcon?.[2] && (
              <Typography
                font="grotesk"
                weight="book"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size={isTablet() ? "subscript-sm" : "body-md"}
                text={isCheckIcon?.[2]}
                className="pl-16 text-start sm:pl-[2px] md:pl-10"
              />
            )}
          </div>
        ) : isCheckIconBlackStroke ? (
          <div
            className={classNames(
              "flex items-center",
              isCheckIconBlackStroke?.[2] && "w-full justify-start"
            )}
          >
            <CircleCheckIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={classNames(
                "shrink-0",
                isCheckIconBlackStroke?.[1] === "yellow"
                  ? "fill-yellow"
                  : isCheckIconBlackStroke?.[1] === "orange"
                    ? "fill-orange"
                    : isCheckIconBlackStroke?.[1] === "blue"
                      ? "fill-blue"
                      : isCheckIconBlackStroke?.[1] === "white"
                        ? "fill-white"
                        : undefined
              )}
              checkmarkClassName={
                isCheckIconBlackStroke?.[1] === "white"
                  ? "stroke-black"
                  : "stroke-white"
              }
            />
            {isCheckIconBlackStroke?.[2] && (
              <Typography
                font="grotesk"
                weight="book"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size={isTablet() ? "subscript-sm" : "body-md"}
                text={isCheckIconBlackStroke?.[2]}
                className="pl-16 text-start sm:pl-[2px] md:pl-10"
              />
            )}
          </div>
        ) : isDashIcon ? (
          <div className="flex items-center">
            <CircleDashIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={
                isDashIcon?.[1] === "yellow"
                  ? "fill-yellow"
                  : isDashIcon?.[1] === "orange"
                    ? "fill-orange"
                    : isDashIcon?.[1] === "blue"
                      ? "fill-blue"
                      : isDashIcon?.[1] === "white"
                        ? "fill-white"
                        : undefined
              }
              dashClassName={
                isDashIcon?.[1] === "white" ? "stroke-black" : "stroke-white"
              }
            />
          </div>
        ) : isHeader ? (
          <Typography
            text={cellData.replace(":header(", "").replace("):", "")}
            size="body-lg"
            font="grotesk"
            weight="medium"
            color={getAccentColor(
              tableTheme === "light" ? Color.White : Color.Charcoal
            )}
          />
        ) : cellData.match(/:yocoLogo:/) ? (
          <Logo
            className={classNames(
              "max-h-32",
              tableTheme === "light" ? "fill-black" : "fill-white"
            )}
            alt="Yoco Logo"
          />
        ) : cellData.match(/:crossIcon:/) ? (
          <CircleCrossIcon
            className={
              tableTheme === "light" ? "stroke-charcoal" : "stroke-white"
            }
          />
        ) : isList ? (
          <div className="flex flex-col gap-0.5">
            {list.map((listItem, index) => (
              <div className="flex gap-2" key={index}>
                <CircleCheckIcon
                  height={12}
                  className={
                    tableTheme === "light" ? "text-charcoal" : "text-white"
                  }
                />
                <Typography
                  text={listItem}
                  size="subscript-sm"
                  font="grotesk"
                  weight="book"
                  color={getAccentColor(
                    tableTheme === "light" ? Color.White : Color.Charcoal
                  )}
                />
              </div>
            ))}
          </div>
        ) : isPriceFromStoryblokSlug ? (
          <Typography
            font="grotesk"
            weight="medium"
            color={tableTheme === "light" ? Color.Charcoal : Color.White}
            size="lead-lg"
            text={"R" + productPrice}
            className={CELL_TEXT_CLASSSES}
          />
        ) : isDetailedPriceFromStoryblokSlug ? (
          <div className="flex flex-col items-start gap-0.5">
            {showPromotion && promotionSavings > 0 ? (
              <Typography
                font="grotesk"
                weight="book"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size="subscript-sm"
                text={"SAVE R" + promotionSavings}
                className={CELL_TEXT_CLASSSES + " opacity-40"}
                leadingNone
              />
            ) : null}
            <div className="flex place-items-baseline gap-2">
              <Typography
                font="grotesk"
                weight="medium"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size="lead-sm"
                text={"R" + productPrice}
                className={CELL_TEXT_CLASSSES}
                leadingNone
              />
              <Typography
                font="grotesk"
                weight="medium"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size="subscript-sm"
                text={priceSideScript ? priceSideScript : ""}
                className={CELL_TEXT_CLASSSES}
                leadingNone
              />
            </div>
            <Typography
              font="grotesk"
              weight="book"
              color={tableTheme === "light" ? Color.Charcoal : Color.White}
              size="subscript-sm"
              text={priceSubScript ? priceSubScript : ""}
              className={CELL_TEXT_CLASSSES + " text-nowrap"}
              leadingNone
            />
          </div>
        ) : isButton && buttonLabel && buttonLink ? (
          <PillButton
            size="medium"
            style="solid"
            text={buttonLabel}
            color={Color.Blue}
            linkUrl={buttonLink}
            trackingEvent="TableButtonEvent"
            trackingEventKey="column"
            trackingEventValue={data[0][columnIndex]}
          />
        ) : (
          <Tooltip
            text={
              isPrice
                ? cellData.replace(":price(", "").replace("):", "")
                : cellData
            }
            backgroundColor={
              tableTheme === "light" ? Color.Charcoal : Color.White
            }
            showTooltipCondition="hoverContentEllipsed"
          >
            <Typography
              font="grotesk"
              weight={isPrice || rowIndex === 0 ? "medium" : "book"}
              color={tableTheme === "light" ? Color.Charcoal : Color.White}
              size={
                isPrice ? "lead-lg" : rowIndex === 0 ? "body-lg" : "body-md"
              }
              text={
                isPrice
                  ? cellData.replace(":price(", "").replace("):", "")
                  : cellData
              }
              className={CELL_TEXT_CLASSSES}
            />
          </Tooltip>
        )}
      </div>
    )
  }

  const renderSwiperSlideHeaders = () => {
    const tableColumns = []
    for (let colIndex = 0; colIndex < columnCount; colIndex++) {
      const columnHeading = data[0][colIndex] || ""
      tableColumns.push(
        <SwiperSlide
          key={`${columnHeading.replace(" ", "-")}-${colIndex}`}
          className="swiper-no-swiping"
        >
          <div
            className={classNames(
              "flex items-center justify-center",
              {
                "h-40":
                  columnCount > numDesktopColumns &&
                  !(columnImages && columnImages.length > 0),
              },
              {
                "h-[92px] lg:h-[128px]":
                  columnImages && columnImages.length > 0,
              }
            )}
          >
            {columnImages && columnImages[colIndex] && (
              <div className="aspect-square h-full w-auto">
                <GatsbyStoryblokImage
                  aspectRatio="fullWidth"
                  image={columnImages[colIndex]
                    .replace(":image(", "")
                    .replace("):", "")}
                  alt={columnHeading}
                  className="h-full w-auto"
                  testId="comparison-table-slide-image"
                />
              </div>
            )}
          </div>
          {RenderCellData(0, colIndex)}
        </SwiperSlide>
      )
    }

    return tableColumns
  }

  const renderSwiperSlideContent = () => {
    const tableColumns = []
    for (let colIndex = 0; colIndex < columnCount; colIndex++) {
      const columnHeading = data[0][colIndex] || ""
      tableColumns.push(
        <SwiperSlide
          key={`${columnHeading.replace(" ", "-")}-${colIndex}`}
          className="swiper-no-swiping"
        >
          {data.map(
            (_, rowIndex) =>
              rowIndex !== 0 && RenderCellData(rowIndex, colIndex)
          )}
        </SwiperSlide>
      )
    }

    return tableColumns
  }

  return (
    <div className="grid grid-cols-2 gap-x-16 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:gap-x-20 lg:gap-x-32">
      {columnCount <= numDesktopColumns && (
        <div className="sticky top-[82px] z-20 col-span-2 flex items-center justify-between sm:hidden">
          <button
            type="button"
            className={classNames(
              `swiper-prev-${sectionId}`,
              "relative my-8 rotate-180 disabled:pointer-events-none disabled:opacity-50",
              tableTheme === "light" ? "text-charcoal" : "text-white"
            )}
          >
            <ArrowIcon animateOnHover={false} />
          </button>
          <div
            className={classNames(
              `swiper-pagination-${sectionId}`,
              "!relative flex items-center px-16 py-8"
            )}
          />
          <button
            type="button"
            className={classNames(
              `swiper-next-${sectionId}`,
              "my-8 disabled:pointer-events-none disabled:opacity-50",
              tableTheme === "light" ? "text-charcoal" : "text-white"
            )}
          >
            <ArrowIcon animateOnHover={false} />
          </button>
        </div>
      )}
      <div className="relative z-10 row-span-2">
        <div
          className={classNames(
            getBackgroundColorClass(backgroundColor),
            "sticky rounded-b-8", // top positioning scales with navbar height
            outerContainer === "gridSection"
              ? "top-[70px] md:top-[83px] lg:top-[90px]"
              : "top-[46px] sm:top-60 lg:top-[62px]"
          )}
        >
          <div
            className={classNames("flex items-end", {
              "h-[92px] lg:h-[128px]":
                columnImages && columnImages.length !== 0,
            })}
          >
            {columnCount > numDesktopColumns && (
              <div
                className={classNames(
                  `swiper-pagination-${sectionId}`,
                  "px-16 py-8"
                )}
              />
            )}
          </div>
          <div className={CELL_CLASSES}>
            <Tooltip
              text={
                rowHeadings[0].match(TableRegex.TITLE)
                  ? rowHeadings[0].replace(":title(", "").replace("):", "")
                  : rowHeadings[0]
              }
              backgroundColor={
                tableTheme === "light" ? Color.Charcoal : Color.White
              }
              showTooltipCondition="hoverContentEllipsed"
            >
              <Typography
                font="grotesk"
                weight="medium"
                color={getAccentColor(
                  tableTheme === "light" ? Color.White : Color.Charcoal
                )}
                size={
                  rowHeadings[0].match(TableRegex.TITLE) ? "body-lg" : "body-md"
                }
                text={
                  rowHeadings[0].match(TableRegex.TITLE)
                    ? rowHeadings[0].replace(":title(", "").replace("):", "")
                    : rowHeadings[0]
                }
                className={classNames(CELL_TEXT_CLASSSES, "text-left")}
              />
            </Tooltip>
          </div>
        </div>
        {rowHeadings.map((rowHeading, index) => {
          if (rowHeading.match(TableRegex.TITLE)) {
            titleRowIndices.current.push(index)
          }
          return (
            index !== 0 && (
              <div
                key={`${rowHeading.replace(" ", "-")}-${index}`}
                className={classNames(
                  CELL_CLASSES,
                  {
                    "mt-16 md:mt-24": rowHeading.match(TableRegex.TITLE),
                  },
                  tableTheme === "light"
                    ? "even:bg-light-gray"
                    : backgroundColor === Color.Charcoal
                      ? "even:bg-black"
                      : "even:bg-charcoal"
                )}
              >
                <Tooltip
                  text={
                    rowHeading.match(TableRegex.TITLE)
                      ? rowHeading.replace(":title(", "").replace("):", "")
                      : rowHeading
                  }
                  backgroundColor={
                    tableTheme === "light" ? Color.Charcoal : Color.White
                  }
                  showTooltipCondition="hoverContentEllipsed"
                >
                  <Typography
                    font="grotesk"
                    weight="medium"
                    color={getAccentColor(
                      tableTheme === "light" ? Color.White : Color.Charcoal
                    )}
                    size={
                      rowHeading.match(TableRegex.TITLE) ? "body-lg" : "body-md"
                    }
                    text={
                      rowHeading.match(TableRegex.TITLE)
                        ? rowHeading.replace(":title(", "").replace("):", "")
                        : rowHeading
                    }
                    className={classNames(CELL_TEXT_CLASSSES, "text-left")}
                  />
                </Tooltip>
              </div>
            )
          )
        })}
      </div>
      <div
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "sticky z-10 rounded-b-8", // top positioning scales with navbar height
          outerContainer === "gridSection"
            ? "top-[70px] md:top-[83px] lg:top-[90px]"
            : "top-[47px] sm:top-[60px] lg:top-[62px]"
        )}
      >
        <Swiper
          id={`heading-swiper-${sectionId}`}
          noSwiping={false}
          spaceBetween={16}
          allowTouchMove={false}
          breakpoints={{
            667: {
              spaceBetween: 0,
              slidesPerView:
                columnCount < numDesktopColumns
                  ? columnCount
                  : numDesktopColumns,
              noSwiping: columnCount > numDesktopColumns ? false : true,
            },
          }}
          navigation={{
            nextEl: `.swiper-next-${sectionId}`,
            prevEl: `.swiper-prev-${sectionId}`,
          }}
          onBeforeInit={(swiper) => {
            headerSwiperRef.current = swiper
          }}
        >
          {renderSwiperSlideHeaders()}
        </Swiper>
      </div>
      <div className="z-1">
        <Swiper
          id={`data-swiper-${sectionId}`}
          noSwiping={false}
          spaceBetween={16}
          pagination={{
            el: `.swiper-pagination-${sectionId}`,
            clickable: true,
            renderBullet: function (_, className) {
              return `<span class="${classNames(
                className,
                "!h-4 !w-4 !mr-4",
                tableTheme === "light" ? "!bg-charcoal" : "!bg-white"
              )}"></span>`
            },
          }}
          breakpoints={{
            667: {
              spaceBetween: 0,
              slidesPerView:
                columnCount < numDesktopColumns
                  ? columnCount
                  : numDesktopColumns,
              noSwiping: columnCount > numDesktopColumns ? false : true,
            },
          }}
          grabCursor
          navigation={{
            nextEl: `.swiper-next-${sectionId}`,
            prevEl: `.swiper-prev-${sectionId}`,
          }}
          onInit={(swiper) => {
            if (headerSwiperRef.current) {
              swiper.controller.control = headerSwiperRef.current
            }
          }}
        >
          {renderSwiperSlideContent()}
        </Swiper>
      </div>
    </div>
  )
}

export default SingleEntryComparisonTable
